import React from 'react';
import { useContext, useState, useEffect } from 'react';
import { Box, Typography, Chip, Card, CardContent, Button } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from '../../../lib/contexts/ThemeContext';
import { OrdersContext } from '../../../lib/contexts/OrdersContext';

const MiniDeliveryDetails = ({ order, smallerWidth = false }) => {
  const navigate = useNavigate();
  const {
    state: { darkMode },
  } = useContext(ThemeContext);
  const {
    actions: { adjustTag },
  } = useContext(OrdersContext);
  const getStatusColor = (status) => {
    if (!status) return 'default';
    switch (status.toLowerCase()) {
      case 'delivered':
        return 'success';
      case 'canceled':
        return 'error';
      case 'scheduled':
        return 'info';
      case 'future':
        return 'default';
      case 'shipped':
        return 'success';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };
  // console.log('order in mini delivery details', order);
  const calculateDaysUntilDelivery = (deliveryDate) => {
    if (!deliveryDate) return 'N/A';
    const today = new Date();
    const delivery = new Date(deliveryDate);
    const differenceInTime = delivery - today;

    if (differenceInTime < 0) return null; // Delivery date is in the past
    return Math.ceil(differenceInTime / (1000 * 60 * 60 * 24)); // Convert to days
  };

  const daysUntilDelivery = calculateDaysUntilDelivery(order.deliveryDate);

  // const handleReportIssue = () => {
  //     console.log(`Reporting issue for order ${order.id}`);
  // };

  const handleNavigate = () => {
    navigate(`/orders/${order.id}`);
  };

  const getHeader = (order) => {
    let displayedInformation;
    let messageLabel;

    if (order.dropoff_time != null) {
      displayedInformation = new Date(order.dropoff_time).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
      messageLabel = 'Delivered:';
    } else if (order.cutoff_time != null) {
      displayedInformation = new Date(order.cutoff_time).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
      messageLabel = 'Editable Until:';
    } else if (order.placed_at != null) {
      displayedInformation = new Date(order.placed_at).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
      messageLabel = 'Placed At:';
    }

    const today = new Date();
    const deliveryDate = new Date(order.dropoff_time);
    const oneWeekFromToday = new Date();
    oneWeekFromToday.setDate(today.getDate() + 7);

    let displayText;
    let typographyStyles;

    if (deliveryDate <= today) {
      displayText = displayedInformation;
      typographyStyles = {
        color: 'var(--color12, #1C124D)',
        leadingTrim: 'both',
        textEdge: 'cap',
        fontFamily: 'Montserrat',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: 'normal',
      };
    } else if (deliveryDate <= oneWeekFromToday) {
      displayText = 'Closed';
      typographyStyles = {
        color: 'var(--Gray-gray11, #6F6F6F)',
        leadingTrim: 'both',
        textEdge: 'cap',
        fontFamily: 'Montserrat',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: 'normal',
      };
    } else {
      const daysLeft = Math.floor((deliveryDate - oneWeekFromToday) / (1000 * 60 * 60 * 24));
      displayText = `${daysLeft} days left`;
      typographyStyles = {
        color: 'var(--secondary-mint-accessilbe, #1D7E6B)',
        leadingTrim: 'both',
        textEdge: 'cap',
        fontFamily: 'Montserrat',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: 'normal',
      };
    }

    return (
      <Box
        id="delivery-date-container"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          alignSelf: 'stretch',
        }}
      >
        <Box
          id="delivery-date-content"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 'var(--3, 13px)',
          }}
        >
          <Typography
            sx={{
              color: 'var(--Gray-gray12, #171717)',
              leadingTrim: 'both',
              textEdge: 'cap',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: 'normal',
            }}
          >
            {messageLabel}
          </Typography>
          <Typography sx={typographyStyles}>{displayText}</Typography>
        </Box>
        {deliveryDate > today ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              alignSelf: 'stretch',
            }}
          >
            <Typography
              sx={{
                color: 'var(--Gray-gray12, #171717)',
                leadingTrim: 'both',
                textEdge: 'cap',
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal',
              }}
            >
              Delivery Date:
            </Typography>
            <Typography
              sx={{
                color: 'var(--color12, #1C124D)',
                leadingTrim: 'both',
                textEdge: 'cap',
                fontFamily: 'Montserrat',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: 'normal',
              }}
            >
              {order.deliveryDate}
            </Typography>
          </Box>
        ) : (
          <Button
            id="feedback-button"
            sx={{
              display: 'flex',
              width: '135px',
              height: 'var(--3, 36px)',
              padding: 'var(--0, 0px) var(--3, 13px)',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 'var(--3, 13px)',
              borderRadius: 'var(--3, 7px)',
              border: 'var(--1, 2px) solid var(--color12, #1C124D)',
              opacity: 0,
            }}
          >
            <Typography
              sx={{
                color: 'var(--color12, #1C124D)',
                textAlign: 'center',
                leadingTrim: 'both',
                textEdge: 'cap',
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: 'normal',
              }}
            >
              Give Feedback
            </Typography>
          </Button>
        )}
      </Box>
    );
  };

  return (
    <Box
      id="mini-delivery-details-container"
      onClick={handleNavigate}
      sx={{
        display: 'flex',
        width: smallerWidth ? '262px' : '330px', //'292px',
        paddingBottom: 'var(--5, 24px)',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '16px',
        alignSelf: 'stretch',
        borderRadius: '8px',
        border: '2px solid var(--color3, #F5F4F9)',
        background: 'var(--Gray-gray2, #F8F8F8)',
        cursor: 'pointer',
      }}
    >
      <Box
        id="mini-delivery-details-header"
        sx={{
          display: 'flex',
          padding: '12px 16px',
          justifyContent: 'space-between',
          alignItems: 'center',
          alignSelf: 'stretch',
          borderRadius: '8px 8px 0px 0px',
          background:
            order &&
            (adjustTag(order) === 'CONFIRMED' || adjustTag(order) === 'PICKED UP' || adjustTag(order) === 'APPROVED')
              ? '#D6D2E7'
              : order &&
                (adjustTag(order) === 'FUTURE' ||
                  adjustTag(order) === 'UP NEXT' ||
                  adjustTag(order) === 'NO HUB' ||
                  adjustTag(order) === 'ASSIGNED' ||
                  adjustTag(order) === 'PLACED')
              ? '#1C124D'
              : 'var(--Gray-gray5, #E8E8E8)',
        }}
      >
        <Box
          id="mini-delivery-details-header-content"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--3, 13px)',
          }}
        >
          <Box
            id="order-number-container"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <Typography
              sx={{
                color:
                  order &&
                  (adjustTag(order) === 'FUTURE' ||
                    adjustTag(order) === 'UP NEXT' ||
                    adjustTag(order) === 'NO HUB' ||
                    adjustTag(order) === 'ASSIGNED' ||
                    adjustTag(order) === 'PLACED')
                    ? '#FDFDFD'
                    : 'var(--Gray-gray12, #171717)',
                leadingTrim: 'both',
                textEdge: 'cap',
                fontFamily: 'Montserrat',
                fontSize: '15px', //'18px'
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: 'normal',
              }}
            >
              Order {order.id}
            </Typography>
          </Box>
          <Box
            id="order-status-container"
            sx={{
              display: 'flex',
              padding: '2px 4px',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '10px',
              borderRadius: '20px',
              background:
                order &&
                (adjustTag(order) === 'CONFIRMED' ||
                  adjustTag(order) === 'PICKED UP' ||
                  adjustTag(order) === 'APPROVED')
                  ? '#1C124D'
                  : order &&
                    (adjustTag(order) === 'FUTURE' ||
                      adjustTag(order) === 'UP NEXT' ||
                      adjustTag(order) === 'NO HUB' ||
                      adjustTag(order) === 'ASSIGNED' ||
                      adjustTag(order) === 'PLACED')
                  ? '#FDFDFD'
                  : order && (adjustTag(order) === 'CANCELED' || adjustTag(order) === 'CANCELLED')
                  ? 'var(--_Tamagui-Red-red11, #CD2B31);'
                  : 'var(--secondary-mint-accessilbe, #1D7E6B)',
            }}
          >
            <Chip
              label={
                <Typography
                  sx={{
                    color:
                      order &&
                      (adjustTag(order) === 'FUTURE' ||
                        adjustTag(order) === 'UP NEXT' ||
                        adjustTag(order) === 'NO HUB' ||
                        adjustTag(order) === 'ASSIGNED' ||
                        adjustTag(order) === 'PLACED')
                        ? 'black'
                        : 'var(--Overlay-White-white12, rgba(255, 255, 255, 0.92))',
                    leadingTrim: 'both',
                    textEdge: 'cap',
                    fontFamily: 'Montserrat',
                    fontSize: '11px', //'14px'
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: 'normal',
                  }}
                >
                  {order ? adjustTag(order).charAt(0).toUpperCase() + adjustTag(order).slice(1).toLowerCase() : ''}
                </Typography>
              }
              sx={{
                display: 'flex',
                padding: '2px 4px',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '10px',
                borderRadius: '20px',
                background:
                  order &&
                  (adjustTag(order) === 'CONFIRMED' ||
                    adjustTag(order) === 'PICKED_UP' ||
                    adjustTag(order) === 'APPROVED')
                    ? '#1C124D'
                    : order &&
                      (adjustTag(order) === 'FUTURE' ||
                        adjustTag(order) === 'UP NEXT' ||
                        adjustTag(order) === 'NO HUB' ||
                        adjustTag(order) === 'ASSIGNED' ||
                        adjustTag(order) === 'PLACED')
                    ? '#FDFDFD'
                    : order &&
                      (adjustTag(order) === 'DELIVERY FAILED' ||
                        adjustTag(order) === 'CANCELED' ||
                        adjustTag(order) === 'CANCELLED')
                    ? 'var(--_Tamagui-Red-red11, #CD2B31);'
                    : 'var(--secondary-mint-accessilbe, #1D7E6B)',
              }}
            />
          </Box>
        </Box>
        <ArrowForwardIcon
          onClick={handleNavigate}
          style={{
            width: 'var(--1-5, 24px)',
            height: 'var(--1-5, 24px)',
            color:
              order &&
              (adjustTag(order) === 'FUTURE' ||
                adjustTag(order) === 'UP NEXT' ||
                adjustTag(order) === 'NO HUB' ||
                adjustTag(order) === 'ASSIGNED' ||
                adjustTag(order) === 'PLACED')
                ? '#FDFDFD'
                : 'var(--Gray-gray12, #171717)',
          }}
        />
      </Box>
      <Box
        id="mini-delivery-details-content"
        sx={{
          display: 'flex',
          padding: '0px var(--3-5, 16px)',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'var(--5, 24px)',
          alignSelf: 'stretch',
        }}
      >
        {getHeader(order)}
      </Box>
    </Box>
  );
};

export default MiniDeliveryDetails;

// {/* <Card
//             variant="outlined"
//             sx={{
//                 maxWidth: '360px',
//                 margin: 'auto',
//                 borderRadius: '8px',
//                 overflow: 'hidden',
//             }}
//         >
//             {/* Header Section */}
//             <Box
//                 sx={{
//                     display: 'flex',
//                     justifyContent: 'space-between',
//                     alignItems: 'center',
//                     backgroundColor: '#000', // Black background for the header
//                     color: '#fff',
//                     paddingY: 1,
//                     paddingX: 2,
//                 }}
//             >
//                 <Typography
//                     variant="subtitle2"
//                     sx={{
//                         fontWeight: 'bold',
//                         color: '#fff',
//                     }}
//                 >
//                     Order {order.id}
//                 </Typography>
//                 <Chip
//                     label={order.status || 'Unknown'} // Handle undefined status gracefully
//                     size="small"
//                     color={getStatusColor(order.status)}
//                     sx={{
//                         backgroundColor: getStatusColor(order.status) === 'default' ? '#E0E0E0' : undefined,
//                         fontSize: '0.75rem',
//                         fontWeight: 'bold',
//                         color: '#000',
//                     }}
//                 />
//                 <ArrowForwardIosIcon sx={{ color: '#fff', cursor: 'pointer' }} onClick={handleNavigate} />
//             </Box>

//             {/* Content Section */}
//             <CardContent>
//                 <Box display="flex" justifyContent="space-between" mb={1}>
//                     <Box>
//                         <Typography
//                             variant="body2"
//                             sx={{ fontWeight: 'bold', color: '#6B7280' }}
//                         >
//                             Expected delivery:
//                         </Typography>
//                         <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
//                             {order.deliveryDate || 'N/A'}
//                         </Typography>
//                     </Box>
//                     {daysUntilDelivery ? (
//                         <Typography
//                             variant="body2"
//                             sx={{
//                                 fontWeight: 'bold',
//                                 color: '#008000', // Green text for "Edit Time"
//                                 display: 'flex',
//                                 alignItems: 'center',
//                             }}
//                         >
//                             {daysUntilDelivery} days left
//                         </Typography>
//                     ) : (
//                         <Button
//                             variant="outlined"
//                             size="small"
//                             sx={{
//                                 textTransform: 'none',
//                                 fontWeight: 'bold',
//                                 color: darkMode ? '#fff' :  '#000',
//                                 borderColor: darkMode ? '#fff' : '#000',
//                                 '&:hover': {
//                                     backgroundColor: darkMode ? '#444' : '#f0f0f0',
//                                 },
//                             }}
//                             onClick={handleReportIssue}
//                         >
//                             Report Issue
//                         </Button>
//                     )}
//                 </Box>
//             </CardContent>
//         </Card> */}
